import { useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { Bell, KeyRound, X } from "lucide-react";
import { requestCautionFeeRefund } from "@services/krent.service";

interface Props {
  transactionId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function RefundRequestModal({ transactionId, onClose, onSuccess }: Props) {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const pending = useRef(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (pending.current) return;
    const pin = digits.join("");
    if (!/^\d{4}$/.test(pin)) {
      setError("Enter your four-digit PIN.");
      inputs.current[digits.findIndex(digit => !digit)]?.focus();
      return;
    }
    pending.current = true;
    setSubmitting(true);
    setError("");
    try {
      await requestCautionFeeRefund(transactionId, pin);
      setDigits(["", "", "", ""]);
      setSuccess(true);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to submit your refund request. Please try again.");
    } finally {
      pending.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Modal show centered onHide={() => !submitting && onClose()} backdrop={submitting ? "static" : true}
      keyboard={!submitting} dialogClassName="cfd-refund-modal" aria-labelledby="refund-modal-title">
      <Modal.Body>
        <button type="button" className="cfd-modal-close" aria-label="Close" disabled={submitting} onClick={onClose}><X size={28} /></button>
        <div className="cfd-modal-icon">{success ? <Bell size={44} /> : <KeyRound size={48} />}</div>
        {success ? (
          <div className="cfd-refund-success" role="status">
            <h2 id="refund-modal-title">Your request will be reviewed</h2>
            <p>within <strong>24–48 hours.</strong> If no damages are found, your refund will be processed to your saved bank account.</p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h2 id="refund-modal-title">Confirm &amp; authorise</h2>
            <p id="refund-pin-help">Enter your caution fee PIN to submit this request.</p>
            <div className="cfd-pin-fields" role="group" aria-label="Four-digit caution fee PIN">
              {digits.map((digit, index) => (
                <input key={index} ref={element => { inputs.current[index] = element; }} type="password" inputMode="numeric"
                  autoComplete="off" autoFocus={index === 0} maxLength={1} placeholder="0" value={digit} disabled={submitting}
                  aria-label={`PIN digit ${index + 1}`} aria-describedby="refund-pin-help" aria-invalid={!!error}
                  onFocus={event => event.target.select()}
                  onChange={event => {
                    const value = event.target.value.replace(/\D/g, "").slice(-1);
                    setDigits(current => current.map((item, position) => position === index ? value : item));
                    setError("");
                    if (value) inputs.current[index + 1]?.focus();
                  }}
                  onKeyDown={event => {
                    if (event.key === "Backspace" && !digit) inputs.current[index - 1]?.focus();
                    if (event.key === "ArrowLeft") inputs.current[index - 1]?.focus();
                    if (event.key === "ArrowRight") inputs.current[index + 1]?.focus();
                  }}
                  onPaste={event => {
                    event.preventDefault();
                    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
                    if (!pasted) return;
                    setDigits(Array.from({ length: 4 }, (_, position) => pasted[position] || ""));
                    setError("");
                    inputs.current[Math.min(pasted.length, 3)]?.focus();
                  }} />
              ))}
            </div>
            {error && <p className="cfd-refund-error" role="alert">{error}</p>}
            <button className="cfd-refund-submit" type="submit" disabled={submitting}>
              {submitting ? "Submitting request…" : "Submit refund request"}
            </button>
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
}
